import { useMemo, useState } from "react";
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";

import CustomerPortalLayout from "components/storefront/CustomerPortalLayout";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import { useAuth } from "context/AuthContext";
import { api, get, post } from "lib/api";
import { isCustomerRole } from "lib/portalRoles";
import useStorefrontCopy from "hooks/useStorefrontCopy";

function prettyStatus(value) {
  return String(value || "not_started")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

function claimStatusColor(status) {
  const normalized = String(status || "")
    .trim()
    .toLowerCase();
  if (
    [
      "approved",
      "rebuilt_verified_boxed",
      "scrapped_parts_banked",
      "closed",
    ].includes(normalized)
  )
    return "success";
  if (normalized === "denied") return "error";
  if (
    [
      "received_for_inspection",
      "inspection_in_progress",
      "rebuild_queued",
      "awaiting_replacement_outcome",
    ].includes(normalized)
  )
    return "info";
  return "warning";
}

function formatDateTime(value) {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleString();
}

function normalizeEntity(row) {
  if (!row) return null;
  if (row?.data) return normalizeEntity(row.data);
  if (row?.attributes)
    return {
      id: row.id,
      documentId: row.documentId || row.id,
      ...row.attributes,
    };
  return row;
}

function nonEmpty(value) {
  const text = String(value || "").trim();
  return text || "";
}

function workflowStepsForClaim(claim) {
  const row = claim || {};
  const stage = String(row?.workflowStage || "")
    .trim()
    .toLowerCase();

  return [
    {
      key: "submitted",
      label: "Claim submitted",
      at: row?.submittedAt || row?.createdAt || null,
      complete: !!(row?.submittedAt || row?.createdAt),
    },
    {
      key: "shippingRequested",
      label: "Shipping option requested",
      at: row?.shippingOptionRequestedAt || null,
      complete: [
        "awaiting_claim_decision",
        "approved",
        "inbound_label_generated",
        "replacement_order_created",
        "replacement_shipped",
        "awaiting_replacement_outcome",
        "received_for_inspection",
        "inspection_in_progress",
        "rebuild_queued",
        "rebuilt_verified_boxed",
        "scrapped_parts_banked",
        "closed",
      ].includes(stage),
    },
    {
      key: "decision",
      label: "Claim approved or denied",
      at: row?.claimDecisionAt || row?.approvedAt || row?.deniedAt || null,
      complete: [
        "approved",
        "inbound_label_generated",
        "replacement_order_created",
        "replacement_shipped",
        "received_for_inspection",
        "inspection_in_progress",
        "rebuild_queued",
        "rebuilt_verified_boxed",
        "scrapped_parts_banked",
        "denied",
        "closed",
      ].includes(stage),
    },
    {
      key: "label",
      label: "Inbound label generated",
      at: row?.approvedAt || row?.updatedAt || null,
      complete: [
        "inbound_label_generated",
        "replacement_order_created",
        "replacement_shipped",
        "received_for_inspection",
        "inspection_in_progress",
        "rebuild_queued",
        "rebuilt_verified_boxed",
        "scrapped_parts_banked",
        "closed",
      ].includes(stage),
    },
    {
      key: "replacement",
      label: "Replacement order / shipment",
      at: row?.outboundShippedAt || row?.replacementApprovalAt || null,
      complete: [
        "replacement_order_created",
        "replacement_shipped",
        "awaiting_replacement_outcome",
        "received_for_inspection",
        "inspection_in_progress",
        "rebuild_queued",
        "rebuilt_verified_boxed",
        "scrapped_parts_banked",
        "closed",
      ].includes(stage),
    },
    {
      key: "replacementOutcome",
      label: "Replacement outcome confirmed or follow-up opened",
      at: row?.replacementOutcomeAt || null,
      complete:
        [
          "received_for_inspection",
          "inspection_in_progress",
          "rebuild_queued",
          "rebuilt_verified_boxed",
          "scrapped_parts_banked",
          "closed",
        ].includes(stage) ||
        ["confirmed_good", "follow_up_claim_created"].includes(
          String(row?.replacementOutcomeStatus || "")
            .trim()
            .toLowerCase()
        ),
    },
    {
      key: "inspection",
      label: "Returned unit received and inspected",
      at: row?.inspectionCompletedAt || row?.returnReceivedAt || null,
      complete: [
        "received_for_inspection",
        "inspection_in_progress",
        "rebuild_queued",
        "rebuilt_verified_boxed",
        "scrapped_parts_banked",
        "closed",
      ].includes(stage),
    },
    {
      key: "disposition",
      label: "Rebuilt or scrapped",
      at: row?.closedAt || row?.inspectionCompletedAt || null,
      complete: [
        "rebuilt_verified_boxed",
        "scrapped_parts_banked",
        "closed",
      ].includes(stage),
    },
  ];
}

async function uploadFiles(fileList) {
  const files = Array.from(fileList || []).filter(Boolean);
  if (!files.length) return [];
  const formData = new FormData();
  files.forEach((file) => formData.append("files", file));
  const uploadRes = await api.post("/api/upload", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  const rows = Array.isArray(uploadRes?.data) ? uploadRes.data : [];
  return rows
    .map((row) => Number(row?.id || 0))
    .filter((id) => Number.isFinite(id) && id > 0);
}

export default function CustomerWarrantyClaimDetail() {
  const { token, pluginRole, authLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { documentId } = useParams();
  const { t } = useStorefrontCopy();
  const isCustomer = isCustomerRole(String(pluginRole || "").toLowerCase());

  const [notice, setNotice] = useState(null);
  const [shippingMode, setShippingMode] = useState("");
  const [shippingSaving, setShippingSaving] = useState(false);
  const [updateMessage, setUpdateMessage] = useState("");
  const [updateFiles, setUpdateFiles] = useState([]);
  const [updateSaving, setUpdateSaving] = useState(false);

  const claimQuery = useQuery({
    queryKey: ["storefront-warranty-claim-detail", documentId],
    queryFn: () => get(`/api/storefront/warranty-claims/${documentId}`),
    enabled: !!token && isCustomer && !!documentId,
    staleTime: 10000,
  });

  const claim = claimQuery.data?.data?.claim || null;
  const shipping = claimQuery.data?.data?.shipping || {};
  const returnRequest = normalizeEntity(claim?.returnRequest);
  const replacementSalesOrder = normalizeEntity(claim?.replacementSalesOrder);
  const supportThread = normalizeEntity(claim?.supportThread);
  const threadDocumentId = String(supportThread?.documentId || "").trim();

  const existingEvidence = useMemo(() => {
    const rows = Array.isArray(claim?.customerEvidenceImages)
      ? claim.customerEvidenceImages
      : [];
    return rows;
  }, [claim]);
  const inspectionImages = useMemo(() => {
    const rows = Array.isArray(returnRequest?.warehouseInspectionImages)
      ? returnRequest.warehouseInspectionImages
      : [];
    return rows;
  }, [returnRequest]);

  const activeShippingMode = String(
    claim?.requestedShippingBookingMode ||
      shipping?.requestedShippingBookingMode ||
      returnRequest?.shippingBookingMode ||
      shipping?.config?.defaultBookingMode ||
      "pickup_plus_label"
  )
    .trim()
    .toLowerCase();
  const selectedShippingMode = shippingMode || activeShippingMode;
  const claimStatus = String(claim?.status || "")
    .trim()
    .toLowerCase();
  const canBookShippingNow =
    shipping?.canBookNow === true ||
    ["approved", "received", "testing"].includes(claimStatus);
  const canRequestShipping =
    shipping?.canRequestShipping !== false && !canBookShippingNow;

  if (!token)
    return (
      <Navigate
        to="/authentication/sign-in"
        state={{ from: location }}
        replace
      />
    );
  if (authLoading || pluginRole == null) return null;
  if (!isCustomer) return <Navigate to="/dashboard" replace />;
  if (!documentId) return <Navigate to="/account/valve-bodies" replace />;

  const handleShippingAction = async () => {
    try {
      setShippingSaving(true);
      setNotice(null);
      const endpoint = canBookShippingNow
        ? `/api/storefront/warranty-claims/${documentId}/shipping/rebook`
        : `/api/storefront/warranty-claims/${documentId}/shipping/request`;
      await post(endpoint, {
        data: {
          bookingMode: selectedShippingMode,
          force: true,
        },
      });
      setNotice({
        type: "success",
        text: canBookShippingNow
          ? "Shipping label regenerated."
          : "Shipping option requested. Your claim is waiting for approval.",
      });
      await claimQuery.refetch();
    } catch (error) {
      setNotice({
        type: "error",
        text:
          error?.response?.data?.error?.message ||
          error?.message ||
          "Unable to update the shipping workflow.",
      });
    } finally {
      setShippingSaving(false);
    }
  };

  const handleClaimUpdate = async () => {
    const trimmed = String(updateMessage || "").trim();
    if (!trimmed && !updateFiles.length) return;
    try {
      setUpdateSaving(true);
      setNotice(null);
      const imageIds = await uploadFiles(updateFiles);
      await post(`/api/storefront/warranty-claims/${documentId}/updates`, {
        data: {
          message: trimmed,
          imageIds,
        },
      });
      setUpdateMessage("");
      setUpdateFiles([]);
      setNotice({
        type: "success",
        text: "Your claim update was saved and shared with support.",
      });
      await claimQuery.refetch();
    } catch (error) {
      setNotice({
        type: "error",
        text:
          error?.response?.data?.error?.message ||
          error?.message ||
          "Unable to save claim update.",
      });
    } finally {
      setUpdateSaving(false);
    }
  };

  return (
    <CustomerPortalLayout
      title={claim?.rmaNumber || "Warranty Claim"}
      subtitle="Track approval, return shipping, replacement, and inspection."
      actions={
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <MDButton
            variant="outlined"
            color="secondary"
            disabled={!threadDocumentId}
            onClick={() =>
              navigate(
                `/account/warranty-claims/${encodeURIComponent(
                  String(documentId || "")
                )}/chat`
              )
            }
          >
            Open chat
          </MDButton>
          <MDButton
            variant="outlined"
            color="info"
            onClick={() =>
              navigate(`/account/valve-bodies/${claim?.unit?.documentId || ""}`)
            }
          >
            {t("registeredUnits.fallbackName", "Valve Body")}
          </MDButton>
          <MDButton
            variant="outlined"
            color="info"
            onClick={() => navigate("/account/valve-bodies")}
          >
            Back
          </MDButton>
        </Box>
      }
    >
      {notice ? (
        <Alert severity={notice.type} sx={{ mb: 2 }}>
          {notice.text}
        </Alert>
      ) : null}
      {claimQuery.error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {claimQuery.error?.response?.data?.error?.message ||
            claimQuery.error?.message ||
            "Failed to load warranty claim."}
        </Alert>
      ) : null}

      {claimQuery.isLoading ? (
        <Alert severity="info">Loading claim details…</Alert>
      ) : !claim ? (
        <Alert severity="warning">Warranty claim not found.</Alert>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12} md={5}>
            <Card variant="outlined" sx={{ p: 2, height: "100%" }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 1,
                  flexWrap: "wrap",
                }}
              >
                <MDTypography variant="h6" fontWeight={900}>
                  Claim overview
                </MDTypography>
                <Chip
                  size="small"
                  variant="outlined"
                  color={claimStatusColor(
                    claim?.workflowStage || claim?.status
                  )}
                  label={prettyStatus(
                    claim?.workflowStage || claim?.status || "open"
                  )}
                />
              </Box>
              <MDTypography
                variant="caption"
                color="text"
                sx={{ opacity: 0.7 }}
              >
                Submitted{" "}
                {formatDateTime(claim?.submittedAt || claim?.createdAt)}
              </MDTypography>
              <Divider sx={{ my: 1 }} />
              <MDTypography variant="button" fontWeight={700} display="block">
                Unit:{" "}
                {claim?.unit?.optionalTag || claim?.unit?.serialNumber || "—"}
              </MDTypography>
              <MDTypography variant="button" display="block">
                Vehicle:{" "}
                {[
                  claim?.vehicleYear,
                  claim?.vehicleMake,
                  claim?.vehicleModel,
                  claim?.vehicleTrim,
                ]
                  .filter(Boolean)
                  .join(" ") || "—"}
              </MDTypography>
              <MDTypography
                variant="caption"
                color="text"
                sx={{ opacity: 0.75 }}
              >
                VIN: {nonEmpty(claim?.vehicleVin) || "—"}
              </MDTypography>
              {nonEmpty(claim?.issueDescription) ? (
                <MDTypography
                  variant="body2"
                  sx={{ mt: 1, whiteSpace: "pre-wrap" }}
                >
                  {claim.issueDescription}
                </MDTypography>
              ) : null}
              {claim?.supportRequired ? (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  {claim?.supportMessage ||
                    "Support team assistance is required for this claim."}
                </Alert>
              ) : null}
              <MDTypography
                variant="caption"
                color="text"
                sx={{ opacity: 0.75, display: "block", mt: 1 }}
              >
                Requested shipping option: {prettyStatus(activeShippingMode)}
              </MDTypography>
              <MDTypography
                variant="caption"
                color="text"
                sx={{ opacity: 0.75, display: "block" }}
              >
                Inspection disposition:{" "}
                {prettyStatus(claim?.inspectionDisposition || "pending")}
              </MDTypography>
              <MDTypography
                variant="caption"
                color="text"
                sx={{ opacity: 0.75, display: "block" }}
              >
                Replacement outcome:{" "}
                {prettyStatus(
                  claim?.replacementOutcomeStatus || "not_required"
                )}
              </MDTypography>
              {String(claim?.requestType || "").toLowerCase() ===
              "first_claim" ? (
                <Box sx={{ mt: 1 }}>
                  <MDTypography
                    variant="caption"
                    color="text"
                    sx={{ opacity: 0.75, display: "block" }}
                  >
                    Replacement finance approval:{" "}
                    {prettyStatus(
                      claim?.replacementApprovalStatus || "not_required"
                    )}
                  </MDTypography>
                  {replacementSalesOrder?.salesOrderNumber ? (
                    <MDTypography
                      variant="caption"
                      color="text"
                      sx={{ opacity: 0.75, display: "block" }}
                    >
                      Replacement order:{" "}
                      {replacementSalesOrder.salesOrderNumber}
                    </MDTypography>
                  ) : null}
                  {claim?.replacementOutcomeNotes ? (
                    <MDTypography
                      variant="caption"
                      color="text"
                      sx={{ opacity: 0.75, display: "block" }}
                    >
                      Replacement notes: {claim.replacementOutcomeNotes}
                    </MDTypography>
                  ) : null}
                </Box>
              ) : null}
              <Box
                sx={{
                  mt: 1.5,
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                }}
              >
                {workflowStepsForClaim(claim).map((step) => (
                  <Box
                    key={step.key}
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <Icon
                      color={step.complete ? "success" : "disabled"}
                      fontSize="small"
                    >
                      {step.complete
                        ? "check_circle"
                        : "radio_button_unchecked"}
                    </Icon>
                    <MDTypography
                      variant="caption"
                      color="text"
                      sx={{ opacity: step.complete ? 0.95 : 0.7 }}
                    >
                      {step.label}{" "}
                      {step.at ? `• ${formatDateTime(step.at)}` : ""}
                    </MDTypography>
                  </Box>
                ))}
              </Box>
            </Card>
          </Grid>

          <Grid item xs={12} md={7}>
            <Card variant="outlined" sx={{ p: 2, height: "100%" }}>
              <MDTypography variant="h6" fontWeight={900} gutterBottom>
                {t("warranty.returnShipping", "Return shipping")}
              </MDTypography>
              <MDTypography
                variant="caption"
                color="text"
                sx={{ opacity: 0.75, display: "block" }}
              >
                Provider: {String(shipping?.provider || "envia").toUpperCase()}{" "}
                • Requested mode: {prettyStatus(activeShippingMode)}
              </MDTypography>
              <MDTypography variant="button" display="block" sx={{ mt: 1 }}>
                Carrier:{" "}
                {nonEmpty(returnRequest?.shippingCarrier) ||
                  nonEmpty(shipping?.config?.defaultCarrier) ||
                  "—"}
              </MDTypography>
              <MDTypography variant="button" display="block">
                Tracking: {nonEmpty(returnRequest?.trackingNumber) || "—"}
              </MDTypography>
              <MDTypography variant="button" display="block">
                Status:{" "}
                {prettyStatus(
                  returnRequest?.status ||
                    returnRequest?.shippingProviderStatus ||
                    "requested"
                )}
              </MDTypography>
              {!canBookShippingNow ? (
                <Alert severity="info" sx={{ mt: 1 }}>
                  Claim is pending approval. Request the shipping option you
                  want and the prepaid label will be generated after approval.
                </Alert>
              ) : null}
              <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
                {nonEmpty(returnRequest?.shippingLabelUrl) ? (
                  <MDButton
                    component="a"
                    href={returnRequest.shippingLabelUrl}
                    target="_blank"
                    rel="noreferrer"
                    variant="outlined"
                    color="info"
                    size="small"
                  >
                    Open prepaid label
                  </MDButton>
                ) : null}
                {nonEmpty(returnRequest?.shippingProviderTrackUrl) ? (
                  <MDButton
                    component="a"
                    href={returnRequest.shippingProviderTrackUrl}
                    target="_blank"
                    rel="noreferrer"
                    variant="outlined"
                    color="secondary"
                    size="small"
                  >
                    Track shipment
                  </MDButton>
                ) : null}
              </Box>
              <Divider sx={{ my: 1.5 }} />
              <TextField
                select
                fullWidth
                size="small"
                label="Shipping option"
                value={selectedShippingMode}
                onChange={(e) => setShippingMode(e.target.value)}
                disabled={!canBookShippingNow && !canRequestShipping}
              >
                {(Array.isArray(shipping?.options) ? shipping.options : []).map(
                  (option) => (
                    <MenuItem
                      key={String(option?.bookingMode || "")}
                      value={String(option?.bookingMode || "")}
                    >
                      {option?.label}
                    </MenuItem>
                  )
                )}
              </TextField>
              <MDTypography
                variant="caption"
                color="text"
                sx={{ opacity: 0.75, mt: 0.75, display: "block" }}
              >
                {selectedShippingMode === "label_only"
                  ? "Label only: print and drop off at the carrier."
                  : "Pickup + label: we request carrier pickup at your address."}
              </MDTypography>
              <Box sx={{ mt: 1 }}>
                <MDButton
                  variant="gradient"
                  color="info"
                  disabled={
                    shippingSaving ||
                    (!canBookShippingNow && !canRequestShipping)
                  }
                  onClick={handleShippingAction}
                >
                  {shippingSaving
                    ? "Saving..."
                    : canBookShippingNow
                    ? "Generate / Regenerate Label"
                    : "Request shipping option"}
                </MDButton>
              </Box>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card variant="outlined" sx={{ p: 2 }}>
              <MDTypography variant="h6" fontWeight={900} gutterBottom>
                Inspection photos and outcome
              </MDTypography>
              <MDTypography
                variant="caption"
                color="text"
                sx={{ opacity: 0.75, display: "block" }}
              >
                Disposition:{" "}
                {prettyStatus(claim?.inspectionDisposition || "pending")}
              </MDTypography>
              <MDTypography
                variant="caption"
                color="text"
                sx={{ opacity: 0.75, display: "block" }}
              >
                Inspection completed:{" "}
                {formatDateTime(
                  claim?.inspectionCompletedAt ||
                    returnRequest?.warehouseInspectedAt
                )}
              </MDTypography>
              {nonEmpty(returnRequest?.warehouseInspectionNotes) ? (
                <MDTypography
                  variant="body2"
                  sx={{ mt: 1, whiteSpace: "pre-wrap" }}
                >
                  {returnRequest.warehouseInspectionNotes}
                </MDTypography>
              ) : (
                <Alert severity="info" sx={{ mt: 1 }}>
                  {t(
                    "warranty.inspectionUpdates",
                    "Inspection updates appear here after the returned item is received."
                  )}
                </Alert>
              )}
              {inspectionImages.length ? (
                <Box
                  sx={{ mt: 1.25, display: "flex", gap: 1, flexWrap: "wrap" }}
                >
                  {inspectionImages.map((file, index) => (
                    <MDButton
                      key={String(file?.documentId || file?.id || index)}
                      component="a"
                      href={String(file?.url || "")}
                      target="_blank"
                      rel="noreferrer"
                      variant="text"
                      color="secondary"
                      size="small"
                      sx={{ px: 0 }}
                    >
                      {file?.name || "Inspection image"}
                    </MDButton>
                  ))}
                </Box>
              ) : null}
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card variant="outlined" sx={{ p: 2 }}>
              <MDTypography variant="h6" fontWeight={900} gutterBottom>
                Add claim update
              </MDTypography>
              <MDTypography
                variant="caption"
                color="text"
                sx={{ opacity: 0.75 }}
              >
                Share diagnostics, extra notes, and photos directly to this
                warranty claim.
              </MDTypography>
              <Box sx={{ mt: 1.25 }}>
                <MDInput
                  fullWidth
                  multiline
                  minRows={3}
                  label="Update message"
                  value={updateMessage}
                  onChange={(e) => setUpdateMessage(e.target.value)}
                />
                <Box sx={{ mt: 1 }}>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={(e) =>
                      setUpdateFiles(Array.from(e.target.files || []))
                    }
                  />
                </Box>
                {updateFiles.length ? (
                  <MDTypography
                    variant="caption"
                    color="text"
                    sx={{ opacity: 0.75, display: "block", mt: 0.5 }}
                  >
                    {updateFiles.length} image
                    {updateFiles.length === 1 ? "" : "s"} selected
                  </MDTypography>
                ) : null}
                <Box sx={{ mt: 1 }}>
                  <MDButton
                    variant="outlined"
                    color="info"
                    disabled={updateSaving}
                    onClick={handleClaimUpdate}
                  >
                    {updateSaving ? "Saving..." : "Save update"}
                  </MDButton>
                </Box>
              </Box>
              {nonEmpty(claim?.customerUpdateNotes) ? (
                <Box sx={{ mt: 2 }}>
                  <Divider sx={{ mb: 1 }} />
                  <MDTypography
                    variant="caption"
                    color="text"
                    sx={{ opacity: 0.75 }}
                  >
                    Latest claim notes
                  </MDTypography>
                  <MDTypography
                    variant="body2"
                    sx={{ whiteSpace: "pre-wrap", mt: 0.5 }}
                  >
                    {claim.customerUpdateNotes}
                  </MDTypography>
                </Box>
              ) : null}
              {existingEvidence.length ? (
                <Box
                  sx={{ mt: 1.25, display: "flex", gap: 1, flexWrap: "wrap" }}
                >
                  {existingEvidence.map((file, index) => (
                    <MDButton
                      key={String(file?.documentId || file?.id || index)}
                      component="a"
                      href={String(file?.url || "")}
                      target="_blank"
                      rel="noreferrer"
                      variant="text"
                      color="info"
                      size="small"
                      sx={{ px: 0 }}
                    >
                      {file?.name || "Image"}
                    </MDButton>
                  ))}
                </Box>
              ) : null}
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card variant="outlined" sx={{ p: 2 }}>
              <MDTypography variant="h6" fontWeight={900} gutterBottom>
                Claim chat
              </MDTypography>
              <MDTypography variant="body2" color="text" sx={{ opacity: 0.8 }}>
                Chat is now on its own page for a cleaner workflow.
              </MDTypography>
              {!threadDocumentId ? (
                <Alert severity="info" sx={{ mt: 1 }}>
                  Chat thread is being prepared for this claim.
                </Alert>
              ) : (
                <Box sx={{ mt: 1 }}>
                  <MDButton
                    variant="outlined"
                    color="secondary"
                    onClick={() =>
                      navigate(
                        `/account/warranty-claims/${encodeURIComponent(
                          String(documentId || "")
                        )}/chat`
                      )
                    }
                  >
                    Open claim chat
                  </MDButton>
                </Box>
              )}
            </Card>
          </Grid>
        </Grid>
      )}
    </CustomerPortalLayout>
  );
}
